import {
  animation as aK,
  backgroundColor,
  backgroundType,
  editable,
  entranceUuid,
  fullWidth,
  hoverEffectUuid,
  moduleStyle,
  moduleTypeSelect,
} from '@/src/tools/contentModulesFieldName';
import { EPreviewSize } from '@/src/types/common/SalesChannel';
import {
  EBackgroundType,
  EModuleWidth,
  EResponsiveResizingOption,
  EnumModuleType,
} from '@/src/types/common/module';
import { get } from 'lodash';
import { checkResponsiveImageCarousel } from 'src/constrains/SalesChannelContent-Revamp/ModuleList/validator';
import {
  animationFields,
  moduleStyleFields,
} from 'src/strategies/render/constant';
import {
  autoPlayCheckbox,
  autoPlayInterval,
  images as iK,
  moduleWidthSelect,
  resizingOptionsSelect,
} from 'src/tools/contentModulesFieldName';
import i18n from '../i18n';
import {
  ModalField,
  ModuleStyleField,
  ResponsiveContainer,
  ResponsiveField,
} from '../util';
import { ANIMATION, CAROUSEL, IMAGES, MOUDLE_STYLE } from './constant';
import { getLayerTemplate } from './layer';

export default (values: any, responsiveType: EPreviewSize) => {
  const carouselSetting = getSettingTemplate(values, responsiveType);
  const moduleStyleSetting = getModuleStyleTemplate(
    values,
    responsiveType,
    EnumModuleType.ResponsiveImageCarousel,
  );
  const animation = getAnimationTemplate(values, responsiveType);
  const images = getImagesTemplate(values, responsiveType);

  return [
    {
      title: i18n['general.content.carousel'],
      id: CAROUSEL,
      field: carouselSetting,
      fields: [
        [moduleWidthSelect],
        [autoPlayCheckbox],
        [autoPlayInterval],
        [resizingOptionsSelect],
      ],
    },
    {
      title: i18n['general.content.moduleStyle'],
      id: MOUDLE_STYLE,
      type: ResponsiveContainer.ModuleStyle,
      field: moduleStyleSetting,
      fields: moduleStyleFields,
    },
    {
      title: i18n['general.content.animation'],
      id: ANIMATION,
      field: animation,
      fields: animationFields,
    },
    {
      title: i18n['general.content.images'],
      id: IMAGES,
      field: images,
      moduleType: EnumModuleType.ResponsiveImageCarousel,
      headerErrorDependencies: [iK], //for section validation
      fields: [[iK]],
      validator: (value, extra) => {
        const i = get(value, 'images');
        const { checkImages } = checkResponsiveImageCarousel();
        return checkImages(i, extra);
      },
    },
  ];
};

export const animation_not_editable = [
  [aK, entranceUuid],
  [aK, hoverEffectUuid],
];

export const moduleStyle_not_editable = [
  [moduleStyle, backgroundType],
  [moduleStyle, backgroundColor],
];

export const not_editable = [
  moduleTypeSelect,
  autoPlayCheckbox,
  autoPlayInterval,
  ...moduleStyle_not_editable,
  ...animation_not_editable,
];

export const getSettingTemplate = (
  values: any,
  responsiveType: EPreviewSize,
) => {
  const v =
    responsiveType === EPreviewSize.WIDE || values[`${CAROUSEL}_${editable}`];
  const commonFields = [
    {
      field: ResponsiveField.CarouselType,
      disabled: responsiveType !== EPreviewSize.WIDE || !v,
      options: [
        {
          label: i18n['content.carouseltype.image'],
          value: EnumModuleType.ResponsiveImageCarousel,
        },
        {
          label: i18n['content.carouseltype.content'],
          value: EnumModuleType.ResponsiveContentCarousel,
        },
        {
          label: i18n['content.carouseltype.product'],
          value: EnumModuleType.ResponsiveProductCarousel,
        },
      ],
    },
    {
      field: ResponsiveField.ModuleWidth,
      disabled: !v,
      options: [
        { label: i18n['general.content.fullWidth'], value: EModuleWidth.FULL },
        { label: i18n['general.content.none'], value: EModuleWidth.NONE },
        { label: i18n['general.content.wideWidth'], value: EModuleWidth.WIDE },
      ],
    },
    {
      field: ResponsiveField.AutoPlay,
      disabled: responsiveType !== EPreviewSize.WIDE || !v,
    },
    {
      field: ResponsiveField.ResizingOption,
      disabled: !v,
      options: [
        {
          label: i18n['general.content.fixed'],
          value: EResponsiveResizingOption.FIXED,
        },
        {
          label: i18n['general.content.scalable'],
          value: EResponsiveResizingOption.SCALABLE,
        },
      ],
    },
  ];
  return commonFields;
};

export const getModuleStyleTemplate = (
  values: any,
  responsiveType: EPreviewSize,
  moduleType: EnumModuleType,
) => {
  const v =
    responsiveType === EPreviewSize.WIDE ||
    values[`${MOUDLE_STYLE}_${editable}`];
  let fields: Array<any> = [
    {
      field: ModuleStyleField.TopPadding,
      disabled: !v,
    },
  ];
  if (values?.moduleWidth !== EModuleWidth.FULL) {
    fields = [
      ...fields,
      {
        field: ModuleStyleField.BackgroundType,
        options: [
          {
            label: i18n['general.backgroundtype.colour'],
            value: EBackgroundType.COLOR,
          },
          {
            label: i18n['general.backgroundtype.none'],
            value: EBackgroundType.NONE,
          },
        ],
        disabled: getModuleStyleDisabled(moduleType)
          ? !v
          : responsiveType !== EPreviewSize.WIDE || !v,
      },
    ];
    if (
      get(values, [moduleStyle, backgroundType]) === EBackgroundType.COLOR ||
      moduleType === EnumModuleType.ResponsiveText
    ) {
      if (
        get(values, [moduleStyle, backgroundType]) !== EBackgroundType.COLOR
      ) {
        fields = [
          ...fields,
          { field: ModuleStyleField.FullWidth, disabled: !v },
          { field: ModuleStyleField.MarginToTop, disabled: !v },
          { field: ModuleStyleField.MarginToBottom, disabled: !v },
        ];
      } else {
        fields = [
          ...fields,
          {
            field: ModuleStyleField.BackgroundColor,
            disabled: getModuleStyleDisabled(moduleType)
              ? !v
              : responsiveType !== EPreviewSize.WIDE || !v,
          },
          { field: ModuleStyleField.FullWidth, disabled: !v },
          { field: ModuleStyleField.MarginToTop, disabled: !v },
          { field: ModuleStyleField.MarginToBottom, disabled: !v },
        ];
      }

      if (!get(values, [moduleStyle, fullWidth])) {
        fields = [
          ...fields,
          { field: ModuleStyleField.MarginToLeft, disabled: !v },
          { field: ModuleStyleField.MarginToRight, disabled: !v },
        ];
      }
    }
  }
  return fields;
};

export const getAnimationTemplate = (
  values: any,
  responsiveType: EPreviewSize,
) => {
  const v =
    responsiveType === EPreviewSize.WIDE || values[`${ANIMATION}_${editable}`];
  return [
    {
      field: ResponsiveField.Animation,
      disabled: responsiveType !== EPreviewSize.WIDE || !v,
    },
  ];
};

export const getImagesTemplate = (
  values: any,
  responsiveType: EPreviewSize,
) => {
  const v =
    responsiveType === EPreviewSize.WIDE || values[`${IMAGES}_${editable}`];
  return [
    {
      field: ResponsiveField.Images,
      hasModal: true,
      disabled: !v,
      modalFunc: getModalTemplate,
    },
  ];
};

export const getModalTemplate = (layerValue: any) => {
  const layerTemplate = getLayerTemplate(layerValue);
  return {
    left: [
      { field: ModalField.Name },
      { field: ModalField.Image },
      { field: ModalField.Layer },
    ],
    middle: layerTemplate.left,
    right: layerTemplate.right,
  };
};

const getModuleStyleDisabled = (moduleType: EnumModuleType) => {
  if (
    moduleType === EnumModuleType.ResponsiveProductCarousel ||
    moduleType === EnumModuleType.ResponsiveContentCarousel ||
    moduleType === EnumModuleType.ResponsivePoster
  ) {
    return true;
  } else {
    return false;
  }
};
